/**
 * The app's default gizmo on Test mode's stand-in object, as on a placed
 * object (SketchObjectGizmoBinding): move it, and scale X/Y = its width /
 * depth about the center (each followed by the object's sync cascade).
 * Rotation is left out: turning the stand-in shows nothing new.
 */

import * as THREE from 'three';
import type { ObjectDefinitionSpec } from 'shared-types';
import type { GizmoAxis, GizmoFrame, GizmoSpace, TransformGizmoBinding } from '../../../potree/personas/tools/modules/transformGizmo/TransformGizmoBinding';
import type { Snapshot } from '../../../potree/personas/tools/ToolUndoRedoManager';
import type { TestInstance } from '../EditorContext';
import { scaleTestInstance, testCenter } from './testInstance';

const HANDLES: ReadonlyArray<string> = ['translation.x', 'translation.y', 'translation.free', 'scale.x', 'scale.y', 'scale.xy'];
/** Dimensions: moved only, their length set by dragging an end (like a placed one). */
const DIMENSION_HANDLES: ReadonlyArray<string> = ['translation.x', 'translation.y', 'translation.free'];

export class TestInstanceGizmoBinding implements TransformGizmoBinding {
  private spec: ObjectDefinitionSpec | null = null;
  private test: TestInstance | null = null;
  private base: TestInstance | null = null;

  /** `write` replaces the test instance (the editor's UI state). */
  constructor(private readonly write: (test: TestInstance) => void) {}

  /** The current object and test instance (kept as they are mid-drag). */
  configure(spec: ObjectDefinitionSpec, test: TestInstance): void {
    this.spec = spec;
    this.test = test;
  }

  getFrame(): GizmoFrame | null {
    if (!this.test) return null;
    const [u, n] = testCenter(this.test);
    return { position: new THREE.Vector3(u, n, 0), quaternion: new THREE.Quaternion() };
  }

  getSpace(): GizmoSpace {
    return 'object';
  }

  getEnabledHandles(): ReadonlyArray<string> | null {
    return this.spec?.dimension ? DIMENSION_HANDLES : HANDLES;
  }

  beginDrag(): void {
    this.base = this.test;
  }

  applyTranslation(worldDelta: THREE.Vector3): void {
    const base = this.base;
    if (!base) return;
    const [u, n] = testCenter(base);
    this.write({ ...base, center: [u + worldDelta.x, n + worldDelta.y] });
  }

  applyRotation(): void {
    // Not offered (see HANDLES).
  }

  applyScale(axes: ReadonlyArray<GizmoAxis>, factor: number): void {
    const base = this.base;
    if (!base || !this.spec || !(factor > 1e-4)) return;
    this.write(scaleTestInstance(this.spec, base, axes, factor));
  }

  /** Test mode keeps no undo history. */
  commitDrag(): Snapshot | null {
    this.base = null;
    return null;
  }

  cancelDrag(): void {
    if (this.base) this.write(this.base);
    this.base = null;
  }
}
